import { isDefined } from '@sgme/fp';
import { useSPIntl } from 'hooks/useIntl';
import type { FormError, FormErrorSeverity, FormRecord } from 'models/formError';
import { useMemo } from 'react';
import type { AlertSeverity } from 'state/notifications/notification.model';
import { translateValidationError } from 'utils/errors/form-validation-errors';

export type TranslatedErrorMessage = Readonly<{
  severity: AlertSeverity;
  htmlMessage: string;
  id: string;
}>;

export type ColorSeverityMapper = (severity: FormErrorSeverity) => AlertSeverity;

const defaultToWarning: ColorSeverityMapper = () => 'warning';
export function useTranslatedErrorMessages<
  Form = FormRecord,
  AllKeys extends string = string,
  KeyPrefix extends string = string,
>(
  errorPrefix: KeyPrefix,
  errors: readonly FormError<Form, AllKeys, KeyPrefix>[],
  mapSeverityToColor: ColorSeverityMapper = defaultToWarning,
): readonly TranslatedErrorMessage[] {
  const intl = useSPIntl();
  return useMemo(
    () =>
      errors
        .map((error, idx): TranslatedErrorMessage | undefined => {
          const translatedValidationError = translateValidationError(error, intl, errorPrefix);
          if (translatedValidationError === undefined) {
            return undefined;
          }
          const alertSeverity = mapSeverityToColor(translatedValidationError.severity);
          return {
            severity: alertSeverity,
            htmlMessage: translatedValidationError.message,
            id: `${error.translationKey}_${idx}`,
          };
        })
        .filter(isDefined),
    [errorPrefix, errors, intl, mapSeverityToColor],
  );
}
